import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MailIcon from '@mui/icons-material/Mail';
import { Alert, Badge, Box, Card, CardContent, CircularProgress, Divider, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChatModal from 'src/features/chat/ChatModal';
import { AppointmentMessaging, UnreadMessageItem } from 'utils';
import { useUnreadMessagesQuery } from '../../features/chat/chat.queries';

interface MessagesWidgetProps {
  practitionerId?: string;
}

function formatRelativeTime(isoTimestamp: string, t: any): string {
  const dt = DateTime.fromISO(isoTimestamp);
  if (!dt.isValid) return '';
  const diff = DateTime.now().diff(dt, ['days', 'hours', 'minutes']);

  if (diff.days >= 1) {
    return dt.toLocaleString({ month: 'short', day: 'numeric' });
  }
  if (diff.hours >= 1) {
    return t('home.messages.relativeTimeHour', { count: Math.floor(diff.hours) });
  }
  if (diff.minutes >= 1) {
    return t('home.messages.relativeTimeMin', { count: Math.floor(diff.minutes) });
  }
  return t('home.messages.relativeTimeNow');
}

export function MessagesWidget({ practitionerId }: MessagesWidgetProps): JSX.Element {
  const { t } = useTranslation();
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentMessaging | null>(null);
  const [chatModalOpen, setChatModalOpen] = useState<boolean>(false);

  const { data: unreadData, isLoading: loading, isError } = useUnreadMessagesQuery(practitionerId);

  const messages = unreadData?.messages || [];
  const unreadCount = unreadData?.unreadCount || 0;
  const error = isError ? t('home.messages.errorLoading') : null;

  const handleMessageClick = (msg: UnreadMessageItem): void => {
    if (!msg.appointmentId || !msg.encounterId || !msg.patientId) {
      console.warn('[MessagesWidget] Missing IDs to open chat:', msg);
      return;
    }

    const firstName = msg.patientName?.split(' ')[0] ?? '';
    const lastName = msg.patientName?.split(' ').slice(1).join(' ') ?? '';

    const appointment: AppointmentMessaging = {
      id: msg.appointmentId,
      encounterId: msg.encounterId,
      patient: {
        id: msg.patientId,
        firstName,
        lastName,
        dateOfBirth: msg.patientDOB ?? '',
        sex: msg.patientSex ?? undefined,
        phone: msg.patientPhone ?? undefined,
      },
      smsModel: {
        recipients: msg.patientPhone
          ? [{ recipientResourceUri: `Patient/${msg.patientId}`, smsNumber: msg.patientPhone }]
          : [],
        hasUnreadMessages: true,
      },
    };

    setSelectedAppointment(appointment);
    setChatModalOpen(true);
  };

  if (loading) {
    return (
      <Card
        sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}
      >
        <CircularProgress />
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', minHeight: '300px' }}>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', boxShadow: 3, borderRadius: 2 }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Badge badgeContent={unreadCount} color="error" overlap="circular">
            <MailIcon color="primary" />
          </Badge>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('home.messages.title')}
          </Typography>
        </Box>
      </Box>

      <CardContent sx={{ flexGrow: 1, p: 0, '&:last-child': { pb: 0 } }}>
        {messages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: '200px',
              p: 3,
              color: 'text.secondary',
            }}
          >
            <ChatBubbleOutlineIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography variant="body1">{t('home.messages.noMessages')}</Typography>
          </Box>
        ) : (
          <Box>
            {messages.map((msg, index) => (
              <Box
                key={msg.id}
                onClick={() => handleMessageClick(msg)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  '&:hover': { bgcolor: 'action.hover' },
                  borderBottom: index < messages.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {msg.patientName || t('home.messages.unknownPatient')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatRelativeTime(msg.timestamp, t)}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: 1.4,
                  }}
                >
                  {msg.preview}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>

      <Divider />
      <Box sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.secondary">
          {unreadCount > 0 ? t('home.messages.totalCountLabel', { count: unreadCount }) : t('home.messages.inboxClean')}
        </Typography>
      </Box>

      {chatModalOpen && selectedAppointment && (
        <ChatModal
          appointment={selectedAppointment}
          onClose={() => setChatModalOpen(false)}
          onMarkAllRead={() => {
            // Opcional: actualizar el estado local para quitar el badge o el mensaje
          }}
        />
      )}
    </Card>
  );
}
